import React, { useState, useEffect } from 'react';
import { BISTPrice, CurrencyPrice, GoldPrice, CryptoPrice } from '../services/types';
import { useAuth } from '../contexts/AuthContext';
import './Watchlist.css';
import { fmtTR } from '../utils/format';

type AssetType = 'stock' | 'currency' | 'gold' | 'crypto';

interface WatchlistItem {
  id: number;
  type: AssetType;
  symbol: string;
  name: string;
  targetPrice: number;
  entryPrice: number;
  addedDate: string;
  alertEnabled: boolean;
}

interface WatchlistProps {
  stocks: BISTPrice[];
  currencies: CurrencyPrice[];
  golds: GoldPrice[];
  cryptos: CryptoPrice[];
}

const Watchlist: React.FC<WatchlistProps> = ({ stocks, currencies, golds, cryptos }) => {
  const { user } = useAuth();
  const [watchlist, setWatchlist] = useState<WatchlistItem[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<WatchlistItem | null>(null);
  const [selectedStock, setSelectedStock] = useState<string>('');
  const [targetPrice, setTargetPrice] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addType, setAddType] = useState<AssetType>('stock');

  // Load watchlist from backend on mount
  useEffect(() => {
    const loadWatchlist = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch('/api/watchlist', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          // Map alertEnabled from DB's alert_enabled
          const mapped = data.map((item: any) => ({
            ...item,
            alertEnabled: !!item.alertEnabled,
          }));
          setWatchlist(mapped);
          console.log('📋 Watchlist backend\'den yüklendi:', mapped.length, 'öğe');
        }
      } catch (error) {
        console.error('Error loading watchlist from backend:', error);
      }
    };

    loadWatchlist();
  }, [user?.id]); // Sadece user ID değiştiğinde çalış

  // Add to watchlist - backend'e gönder
  const addToWatchlist = async () => {
    if (!selectedStock || !targetPrice) return;

    let entryPrice = 0;
    let name = selectedStock;
    if (addType === 'stock') {
      const s = stocks.find(s => s.StockCode === selectedStock);
      if (!s) return;
      entryPrice = s.LastTransactionPrice;
    } else if (addType === 'currency') {
      const c = currencies.find(c => c.Code === selectedStock);
      if (!c) return;
      entryPrice = c.SellPrice;
      name = c.Name || c.Code;
    } else if (addType === 'gold') {
      const g = golds.find(g => g.Type === selectedStock);
      if (!g) return;
      entryPrice = g.Price;
      name = g.Type;
    } else if (addType === 'crypto') {
      const cr = cryptos.find(cr => cr.Symbol === selectedStock);
      if (!cr) return;
      entryPrice = cr.Price;
      name = cr.Name || cr.Symbol;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch('/api/watchlist', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: addType,
          symbol: selectedStock,
          name,
          targetPrice: parseFloat(targetPrice),
          entryPrice,
        })
      });

      if (response.ok) {
        const newItem = await response.json();
        setWatchlist(prev => [...prev, { ...newItem, type: newItem.type || addType }]);
        console.log('✅ Varlık eklendi:', newItem.symbol);
      }
    } catch (error) {
      console.error('Error adding to watchlist:', error);
    }

    // Form'u temizle
    setSelectedStock('');
    setTargetPrice('');
    setShowAddModal(false);
  };

  // Delete from watchlist - backend'den sil
  const deleteFromWatchlist = async (id: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`/api/watchlist/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setWatchlist(prev => prev.filter(item => item.id !== id));
        console.log('�️ Hisse silindi:', id);
      }
    } catch (error) {
      console.error('Error deleting from watchlist:', error);
    }
  };

  const removeFromWatchlist = (symbol: string) => {
    // Backend'den silmek için ID ile silme gerekli
    const item = watchlist.find(item => item.symbol === symbol);
    if (item) {
      deleteFromWatchlist(item.id);
    }
  };

  const toggleAlert = async (item: WatchlistItem) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const newValue = !item.alertEnabled;
      const response = await fetch(`/api/watchlist/${item.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ alertEnabled: newValue })
      });

      if (response.ok) {
        setWatchlist(prev => prev.map(w =>
          w.id === item.id ? { ...w, alertEnabled: newValue } : w
        ));
        console.log(`🔔 ${item.symbol} uyarı ${newValue ? 'aktif' : 'pasif'}`);
      }
    } catch (error) {
      console.error('Error toggling alert:', error);
    }
  };

  const openEditModal = (item: WatchlistItem) => {
    setEditingItem(item);
    setSelectedStock(item.symbol);
    setTargetPrice(item.targetPrice.toString());
    setSearchTerm(item.symbol);
    setShowEditModal(true);
  };

  const updateWatchlistItem = async () => {
    if (!editingItem || !targetPrice) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`/api/watchlist/${editingItem.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ targetPrice: parseFloat(targetPrice) })
      });

      if (response.ok) {
        setWatchlist(prev => prev.map(item =>
          item.id === editingItem.id
            ? { ...item, targetPrice: parseFloat(targetPrice) }
            : item
        ));
      }
    } catch (error) {
      console.error('Error updating watchlist item:', error);
    }

    setShowEditModal(false);
    setEditingItem(null);
    setSelectedStock('');
    setTargetPrice('');
  };

  const filteredStocks = addType === 'stock'
    ? stocks.filter(s => s.StockCode.toLowerCase().includes(searchTerm.toLowerCase()))
    : addType === 'currency'
    ? currencies.filter(c => c.Code.toLowerCase().includes(searchTerm.toLowerCase()) || (c.Name || '').toLowerCase().includes(searchTerm.toLowerCase()))
    : addType === 'gold'
    ? golds.filter(g => g.Type.toLowerCase().includes(searchTerm.toLowerCase()))
    : cryptos.filter(cr => cr.Symbol.toLowerCase().includes(searchTerm.toLowerCase()) || (cr.Name || '').toLowerCase().includes(searchTerm.toLowerCase()));

  const getCurrentPrice = (item: WatchlistItem): number => {
    if (item.type === 'stock' || !item.type) {
      return stocks.find(s => s.StockCode === item.symbol)?.LastTransactionPrice || 0;
    } else if (item.type === 'currency') {
      return currencies.find(c => c.Code === item.symbol)?.SellPrice || 0;
    } else if (item.type === 'gold') {
      return golds.find(g => g.Type === item.symbol)?.Price || 0;
    } else {
      return cryptos.find(cr => cr.Symbol === item.symbol)?.Price || 0;
    }
  };

  const getCurrentChangePercent = (item: WatchlistItem): number | undefined => {
    if (item.type === 'stock' || !item.type) {
      return stocks.find(s => s.StockCode === item.symbol)?.ChangePercent;
    } else if (item.type === 'currency') {
      return currencies.find(c => c.Code === item.symbol)?.ChangePercent;
    } else if (item.type === 'gold') {
      return golds.find(g => g.Type === item.symbol)?.ChangePercent;
    } else {
      return cryptos.find(cr => cr.Symbol === item.symbol)?.ChangePercent;
    }
  };

  const getTypeLabel = (type: AssetType) => {
    switch (type) {
      case 'stock': return '📊 Hisse';
      case 'currency': return '💱 Döviz';
      case 'gold': return '🥇 Altın';
      case 'crypto': return '₿ Kripto';
      default: return '📊 Hisse';
    }
  };

  const getPricePrefix = (item: WatchlistItem) => {
    if (item.type === 'crypto') return '$';
    return '₺';
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setShowSuggestions(value.length > 0);
  };

  const handleInputFocus = () => {
    setShowSuggestions(searchTerm.length > 0);
  };

  const handleInputBlur = () => {
    // Delay hiding suggestions to allow click events
    setTimeout(() => setShowSuggestions(false), 200);
  };

  return (
    <div className="watchlist-container">
      <div className="watchlist-header">
        <h2>📈 Takip Listesi</h2>
        <button 
          className="add-stock-button"
          onClick={() => setShowAddModal(true)}
        >
          + Varlık Ekle
        </button>
      </div>

      {watchlist.length === 0 ? (
        <div className="empty-watchlist">
          <p>Henüz takip edilen varlığınız bulunmuyor.</p>
          <button onClick={() => setShowAddModal(true)}>
            İlk Varlığınızı Ekleyin
          </button>
        </div>
      ) : (
        <div className="watchlist-table">
          <table>
            <thead>
              <tr>
                <th>Tür</th>
                <th>Sembol</th>
                <th>Anlık Fiyat</th>
                <th>Giriş Fiyatı</th>
                <th>Hedef Fiyat</th>
                <th>Giriş Farkı</th>
                <th>Hedef Farkı</th>
                <th>Tarih</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {watchlist.map((item) => {
                const currentPrice = getCurrentPrice(item);
                const changePercent = getCurrentChangePercent(item);
                const prefix = getPricePrefix(item);
                const entryPrice = item.entryPrice || 0;
                const targetPrice = item.targetPrice || 0;
                const entryDiff = currentPrice - entryPrice;
                const entryDiffPercent = entryPrice > 0 ? (entryDiff / entryPrice) * 100 : 0;
                const targetDiff = currentPrice - targetPrice;
                const targetDiffPercent = targetPrice > 0 ? (targetDiff / targetPrice) * 100 : 0;
                
                return (
                  <tr key={item.id}>
                    <td className="stock-type">{getTypeLabel(item.type)}</td>
                    <td className="stock-symbol">{item.symbol}</td>
                    <td className="current-price">
                      {prefix}{fmtTR(currentPrice)}
                      {changePercent !== undefined && (
                        <span className={`price-change ${changePercent > 0 ? 'positive' : 'negative'}`}>
                          {changePercent > 0 ? '+' : ''}{fmtTR(changePercent)}%
                        </span>
                      )}
                    </td>
                    <td className="entry-price">{prefix}{fmtTR(item.entryPrice) || '0,00'}</td>
                    <td className="target-price">{prefix}{fmtTR(item.targetPrice) || '0,00'}</td>
                    <td className={`entry-diff ${entryDiff > 0 ? 'positive' : 'negative'}`}>
                      {entryDiff > 0 ? '+' : ''}{prefix}{fmtTR(entryDiff)}
                      <br />
                      <small>({entryDiffPercent > 0 ? '+' : ''}{fmtTR(entryDiffPercent)}%)</small>
                    </td>
                    <td className={`target-diff ${targetDiff > 0 ? 'positive' : 'negative'}`}>
                      {targetDiff > 0 ? '+' : ''}{prefix}{fmtTR(targetDiff)}
                      <br />
                      <small>({targetDiffPercent > 0 ? '+' : ''}{fmtTR(targetDiffPercent)}%)</small>
                    </td>
                    <td className="added-date">
                      {new Date(item.addedDate).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="actions">
                      <div className="action-buttons-row">
                        <button
                          className={`action-icon-btn alert-btn ${item.alertEnabled ? 'active' : ''}`}
                          onClick={() => toggleAlert(item)}
                          title={item.alertEnabled ? 'Uyarı aktif — hedefe ulaşınca bildirim gelecek' : 'Uyarı pasif — tıklayarak aktifleştir'}
                        >
                          {item.alertEnabled ? '🔔' : '🔕'}
                        </button>
                        <button
                          className="action-icon-btn edit-btn"
                          onClick={() => openEditModal(item)}
                          title="Hedef fiyatı düzenle"
                        >
                          ✏️
                        </button>
                        <button
                          className="action-icon-btn delete-btn"
                          onClick={() => removeFromWatchlist(item.symbol)}
                          title="Takipten kaldır"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Stock Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>📈 Varlık Ekle</h3>
              <button 
                className="close-button"
                onClick={() => setShowAddModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Varlık Türü:</label>
                <select
                  value={addType}
                  onChange={(e) => { setAddType(e.target.value as AssetType); setSearchTerm(''); setSelectedStock(''); }}
                  className="stock-select"
                >
                  <option value="stock">📊 Hisse (BIST)</option>
                  <option value="currency">💱 Döviz</option>
                  <option value="gold">🥇 Altın</option>
                  <option value="crypto">₿ Kripto</option>
                </select>
              </div>

              <div className="form-group">
                <label>Ara:</label>
                <div className="autocomplete-container">
                  <input
                    type="text"
                    placeholder={addType === 'stock' ? 'Hisse sembolü ile arayın...' : addType === 'currency' ? 'Döviz kodu (USD, EUR)...' : addType === 'gold' ? 'Altın türü...' : 'Kripto sembolü (BTC, ETH)...'}
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    className="search-input"
                  />
                  {showSuggestions && (filteredStocks as any[]).length > 0 && (
                    <div className="suggestions-list">
                      {(filteredStocks as any[]).slice(0, 8).map((item: any) => {
                        const code = addType === 'stock' ? item.StockCode : addType === 'currency' ? item.Code : addType === 'gold' ? item.Type : item.Symbol;
                        const price = addType === 'stock' ? item.LastTransactionPrice : addType === 'currency' ? item.SellPrice : item.Price;
                        const prefix = addType === 'crypto' ? '$' : '₺';
                        const cp = item.ChangePercent;
                        return (
                          <div
                            key={code}
                            className="suggestion-item"
                            onClick={() => { setSelectedStock(code); setSearchTerm(code); setShowSuggestions(false); }}
                          >
                            <div className="suggestion-symbol">{code}</div>
                            <div className="suggestion-price">{prefix}{fmtTR(price)}</div>
                            <div className={`suggestion-change ${cp && cp > 0 ? 'positive' : 'negative'}`}>
                              {cp && cp > 0 ? '+' : ''}{fmtTR(cp)}%
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Hedef Fiyat:</label>
                <input
                  type="number"
                  placeholder="Hedef fiyatı girin..."
                  value={targetPrice}
                  onChange={(e) => setTargetPrice(e.target.value)}
                  className="price-input"
                  step="0.01"
                  min="0"
                />
              </div>

              {selectedStock && (
                <div className="stock-info">
                  <p>
                    <strong>Seçilen:</strong> {selectedStock} ({getTypeLabel(addType)})
                  </p>
                  <p>
                    <strong>Mevcut Fiyat:</strong> {addType === 'crypto' ? '$' : '₺'}{fmtTR(
                      addType === 'stock' ? stocks.find(s => s.StockCode === selectedStock)?.LastTransactionPrice
                      : addType === 'currency' ? currencies.find(c => c.Code === selectedStock)?.SellPrice
                      : addType === 'gold' ? golds.find(g => g.Type === selectedStock)?.Price
                      : cryptos.find(cr => cr.Symbol === selectedStock)?.Price
                    )}
                  </p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button 
                className="cancel-button"
                onClick={() => setShowAddModal(false)}
              >
                İptal
              </button>
              <button 
                className="add-button"
                onClick={addToWatchlist}
                disabled={!selectedStock || !targetPrice}
              >
                Ekle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Stock Modal */}
      {showEditModal && editingItem && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>✏️ Hisseyi Düzenle</h3>
              <button 
                className="close-button"
                onClick={() => setShowEditModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Hisse:</label>
                <input
                  type="text"
                  value={editingItem.symbol}
                  disabled
                  className="stock-input"
                  style={{ backgroundColor: 'var(--bg)', color: 'var(--text-muted)' }}
                />
                <small style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                  Hisse sembolü değiştirilemez
                </small>
              </div>

              <div className="form-group">
                <label>Giriş Fiyatı:</label>
                <input
                  type="text"
                  value={`₺${fmtTR(editingItem.entryPrice) || '0,00'}`}
                  disabled
                  className="price-input"
                  style={{ backgroundColor: 'var(--bg)', color: 'var(--text-muted)' }}
                />
                <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                  Takibe alındığı zaman ki fiyat (değiştirilemez)
                </small>
              </div>

              <div className="form-group">
                <label>Yeni Hedef Fiyat:</label>
                <input
                  type="number"
                  placeholder="Yeni hedef fiyatı girin..."
                  value={targetPrice}
                  onChange={(e) => setTargetPrice(e.target.value)}
                  className="price-input"
                  step="0.01"
                  min="0"
                />
              </div>

              <div className="stock-info">
                <p>
                  <strong>Varlık:</strong> {editingItem.symbol} ({getTypeLabel(editingItem.type)})
                </p>
                <p>
                  <strong>Mevcut Fiyat:</strong> {getPricePrefix(editingItem)}{fmtTR(getCurrentPrice(editingItem))}
                </p>
                <p>
                  <strong>Eski Hedef Fiyat:</strong> {getPricePrefix(editingItem)}{fmtTR(editingItem.targetPrice)}
                </p>
                <p>
                  <strong>Yeni Hedef Fiyat:</strong> {getPricePrefix(editingItem)}{targetPrice || 'Belirtilmemiş'}
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="cancel-button"
                onClick={() => setShowEditModal(false)}
              >
                İptal
              </button>
              <button 
                className="update-button"
                onClick={updateWatchlistItem}
                disabled={!targetPrice}
              >
                Güncelle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Watchlist;
